import { resolvePageContext } from '../core/engine/pipeline/page-context'
import { DataClassName } from '@/Data/const'

/**
 * Opening suggestion chips shown on an empty conversation, keyed by the page the
 * user is on. Values are i18n keys under `components.aiChatBot.suggestions`.
 *
 * List pages get entity-specific prompts (Marina's suggested actions); detail and
 * dashboard pages fall back to the single "Summarize this page" chip. Some list
 * prompts are filter intents that route through build_filter when clicked;
 * here we only supply the prompt text.
 */
const SUMMARIZE_PAGE_KEY = 'components.aiChatBot.suggestions.summarizePage'

// Offered as the first chip when the current list page has a filter applied, so the
// user can turn it into a saved segment in one click (CRM-1451). Only for entities a
// segment can hold (Company / Person) — see SEGMENT_SAVABLE_CLASSES.
const SAVE_FILTER_AS_SEGMENT_KEY =
  'components.aiChatBot.suggestions.list.saveFilterAsSegment'

// Entities whose applied list filter can be saved as a segment (segments hold
// Company or Person records).
const SEGMENT_SAVABLE_CLASSES = new Set<string>([
  DataClassName.COMPANY,
  DataClassName.PERSON,
])

const LIST_SUGGESTIONS: Record<string, string[]> = {
  [DataClassName.ACTIVITY]: [
    'components.aiChatBot.suggestions.list.activity.overdue',
    'components.aiChatBot.suggestions.list.activity.today',
    'components.aiChatBot.suggestions.list.activity.create',
  ],
  [DataClassName.COMPANY]: [
    'components.aiChatBot.suggestions.list.company.find',
    'components.aiChatBot.suggestions.list.company.noRecentActivity',
    'components.aiChatBot.suggestions.list.company.create',
  ],
  [DataClassName.PERSON]: [
    'components.aiChatBot.suggestions.list.person.find',
    'components.aiChatBot.suggestions.list.person.noRecentActivity',
    'components.aiChatBot.suggestions.list.person.create',
  ],
  [DataClassName.LIST]: [
    'components.aiChatBot.suggestions.list.segment.noContacts',
    'components.aiChatBot.suggestions.list.segment.mine',
    'components.aiChatBot.suggestions.list.segment.create',
  ],
}

/**
 * Resolve the suggestion i18n keys for the current page. Always returns at least
 * one key (the summarize fallback), so an unknown context never yields an empty
 * state or a crash.
 */
export function getOpeningSuggestionKeys(): string[] {
  const { mode, dataClass, urlQuery } = resolvePageContext()

  if (mode === 'list' && dataClass) {
    // my-contacts resolves to the generic Contact class; treat it as Person.
    const key =
      dataClass === DataClassName.CONTACT ? DataClassName.PERSON : dataClass
    const suggestions = LIST_SUGGESTIONS[key]
    if (suggestions && suggestions.length > 0) {
      // A filter is applied when the URL carries a saved-filter id (`filter`) or an
      // inline encoded filter (`query`). Offer the save-as-segment chip only then,
      // and only for entities a segment can hold.
      const hasActiveFilter = !!(urlQuery?.filter || urlQuery?.query)
      if (hasActiveFilter && SEGMENT_SAVABLE_CLASSES.has(key)) {
        return [SAVE_FILTER_AS_SEGMENT_KEY, ...suggestions]
      }
      return suggestions
    }
  }

  return [SUMMARIZE_PAGE_KEY]
}
